import React from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { TaskyButton } from '../button/TaskyButton';

type Tone = 'destructive' | 'warning' | 'info';

export interface TaskyConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  tone?: Tone;
  icon?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  /** true ならキャンセルボタンを描画しない（OK を押すまで閉じない確認。旧 alert の置き換え用）。
   *  背景クリックでの onClose は従来どおり効く。 */
  hideCancel?: boolean;
  className?: string;
}

const toneClass: Record<Tone, { bg: string; text: string; button: 'destructive' | 'default' }> = {
  destructive: {
    bg: 'bg-destructive/10',
    text: 'text-destructive',
    button: 'destructive',
  },
  warning: {
    bg: 'bg-warning/10',
    text: 'text-warning',
    button: 'default',
  },
  info: {
    bg: 'bg-primary/10',
    text: 'text-primary',
    button: 'default',
  },
};

/** ダイアログ内でフォーカスを受け取れる要素。フォーカストラップの循環対象。 */
const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const TaskyConfirmDialog: React.FC<TaskyConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  tone = 'destructive',
  icon,
  title,
  description,
  confirmText = '実行',
  cancelText = 'キャンセル',
  hideCancel = false,
  className,
}) => {
  const baseId = React.useId();
  const titleId = `${baseId}-title`;
  const descriptionId = `${baseId}-description`;
  const panelRef = React.useRef<HTMLDivElement>(null);
  const confirmRef = React.useRef<HTMLButtonElement>(null);
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const previouslyFocusedRef = React.useRef<HTMLElement | null>(null);

  // 開いたらダイアログ内へフォーカスを移し、閉じたら元の要素へ戻す。
  // 破壊的な確認は誤確定を避けるためキャンセルから始める。
  React.useEffect(() => {
    if (!open) return undefined;
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    const initial = tone === 'destructive' && !hideCancel ? cancelRef.current : confirmRef.current;
    initial?.focus();
    return () => {
      previouslyFocusedRef.current?.focus?.();
    };
  }, [open, tone, hideCancel]);

  // Escape はキャンセル扱い（hideCancel でも背景クリックと同じく閉じられる）。
  // Tab / Shift+Tab はダイアログ内で循環させる。
  React.useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const panel = panelRef.current;
      if (!panel) return;
      const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusables.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      const inside = active instanceof Node && panel.contains(active);

      if (event.shiftKey ? active === first || !inside : active === last || !inside) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;
  const t = toneClass[tone];

  return createPortal(
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      // window.confirm の置き換え先なので最前面に出す。旧モーダル（z-[1000]）の中から開いても
      // 背面に隠れず、右上の固定ヘッダ（z-[1000]）の上に暗幕がかかるようにする。
      // トースト（z-[10001]）はこの上に残す。
      className="fixed inset-0 z-[10000] bg-black/40 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={panelRef}
        className={cn(
          'bg-white rounded-tasky-lg w-full max-w-md p-6 text-center shadow-card',
          className,
        )}
      >
        <div
          className={cn(
            'mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4 [&_svg]:size-7',
            t.bg,
            t.text,
          )}
        >
          {icon ?? <AlertTriangle />}
        </div>
        <h4 id={titleId} className="text-lg font-semibold text-dark mb-2 whitespace-pre-line">
          {title}
        </h4>
        {description && (
          <p id={descriptionId} className="text-sm text-gray-500 mb-4 whitespace-pre-line">
            {description}
          </p>
        )}
        <div className="flex justify-end gap-3">
          {!hideCancel && (
            <TaskyButton ref={cancelRef} variant="outline" size="sm" onClick={onClose}>
              {cancelText}
            </TaskyButton>
          )}
          <TaskyButton ref={confirmRef} variant={t.button} size="sm" onClick={onConfirm}>
            {confirmText}
          </TaskyButton>
        </div>
      </div>
    </div>,
    document.body,
  );
};
